/**
 * `useParties` / `useParty` — live subscriptions on the org-wide
 * `parties` collection.
 *
 * Parties are org-scoped (no projectId). The `useProjectParties` hook
 * (separate file) derives a project's parties via transactions /
 * tasks / attendance referencing the party id.
 */
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';
import { useFullOrganization } from '@/lib/useFullOrganization';
import type { Party } from '@/lib/parties';

export type UsePartiesResult = {
  parties: Party[];
  loading: boolean;
  error: string | null;
  orgId: string | null;
};

export function useParties(): UsePartiesResult {
  const { org, loading: orgLoading } = useFullOrganization();
  const [items, setItems] = useState<Party[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgId = org?.id ?? null;

  useEffect(() => {
    if (orgLoading) return;
    if (!orgId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      query(collection(db, 'parties'), where('orgId', '==', orgId)),
      (snap) => {
        setItems(
          snap.docs.map((d) => ({ ...(d.data() as Party), id: d.id })),
        );
        setLoading(false);
      },
      (err) => {
        console.warn('[useParties] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [orgId, orgLoading]);

  // Alphabetical sort by name.
  const sorted = useMemo(
    () => [...items].sort((a, b) => a.name.localeCompare(b.name)),
    [items],
  );

  return { parties: sorted, loading, error, orgId };
}

/** Single party doc subscription — used by the detail / preview panel. */
export function useParty(partyId: string | undefined): {
  party: Party | null;
  loading: boolean;
  error: string | null;
} {
  const [p, setP] = useState<Party | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!partyId) {
      setP(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      doc(db, 'parties', partyId),
      (snap) => {
        if (!snap.exists()) setP(null);
        else setP({ ...(snap.data() as Party), id: snap.id });
        setLoading(false);
      },
      (err) => {
        console.warn('[useParty] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [partyId]);

  return { party: p, loading, error };
}
